import { useCallback, useEffect, useMemo, useState, type MouseEvent } from "react";
import CheckIcon from "@mui/icons-material/Check";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import FormHelperText from "@mui/material/FormHelperText";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Popover from "@mui/material/Popover";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { useBrandThemeColors } from "../../../branding";
import {
  useConfigurableConcepts,
  type ConceptFetcher,
} from "../../../hooks/useConfigurableConcepts";
import { usePaginatedConceptValues } from "../../../hooks/usePaginatedConceptValues";
import { getConceptDisplayLabel } from "../../../services/autoTaggingService";
import { AI_PLAN_TOOLTIP_SLOT_PROPS } from "../TruncatedTooltipCell";
import {
  ASSESSMENT_TEMPLATE_OPTIONS,
  CLASSIFICATION_LAYER_INFO,
  EXECUTION_OPTIONS,
  TAGGING_APPROACH_OPTIONS,
  formatClassificationLayerLabel,
  formatUpdateAssessmentRecordDisplay,
  getClassificationLayerOptions,
  shouldShowUpdateRecord,
  type ClassificationPlanConfigState,
} from "./classificationPlanConfigOptions";

const UPDATE_RECORD_COLUMN_HEIGHT = 220;
const UPDATE_RECORD_OPTION_HOVER_BG = "#F3F6FE";
const UPDATE_RECORD_OPTION_SELECTED_BG = "#F3F6FE";

const updateRecordColumnScrollSx = {
  height: UPDATE_RECORD_COLUMN_HEIGHT,
  minHeight: 0,
  overflowY: "auto",
  overflowX: "hidden",
  scrollbarWidth: "thin",
  scrollbarColor: "#C4C4C4 transparent",
  "&::-webkit-scrollbar": { width: "6px" },
  "&::-webkit-scrollbar-thumb": {
    backgroundColor: "#C4C4C4",
    borderRadius: "3px",
  },
} as const;

const formFieldLabelSx = {
  fontSize: "0.875rem",
  fontWeight: 500,
  color: "#262626",
  mb: 0.75,
};

const triggerButtonSx = (error: boolean, hasValue: boolean) => ({
  justifyContent: "space-between" as const,
  height: 38,
  minHeight: 38,
  px: 1.5,
  borderRadius: "3px",
  borderColor: error ? "error.main" : "#D1D5DB",
  color: `${hasValue ? "#0D0D0D" : "#99A1AF"} !important`,
  textTransform: "none" as const,
  fontWeight: 400,
  fontSize: "0.8125rem",
  bgcolor: "#fff",
  "&:hover": {
    borderColor: error ? "error.main" : "#9CA3AF",
    bgcolor: "#F9FAFB",
    color: `${hasValue ? "#0D0D0D" : "#99A1AF"} !important`,
  },
  // Keep gray outline on focus — avoid theme primary blue ring on edit open / tab.
  "&:focus, &.Mui-focusVisible": {
    borderColor: error ? "error.main" : "#D1D5DB",
    outline: "none",
    boxShadow: "none",
  },
  "&.Mui-disabled": {
    borderColor: "#E5E7EB",
    color: `${hasValue ? "#0D0D0D" : "#99A1AF"} !important`,
    bgcolor: "#fff",
    opacity: 1,
    WebkitTextFillColor: hasValue ? "#0D0D0D" : "#99A1AF",
  },
  "& .MuiButton-endIcon": { ml: 1 },
  "& .MuiButton-endIcon .MuiSvgIcon-root": {
    color: "#9CA3AF !important",
    fontSize: 18,
  },
});

type SimpleSelectFieldProps = Readonly<{
  label: string;
  required?: boolean;
  value: string;
  placeholder: string;
  options: readonly string[];
  error?: boolean;
  helperText?: string;
  disabled?: boolean;
  width?: number | string;
  onChange: (next: string) => void;
}>;

function SimpleSelectField({
  label,
  required,
  value,
  placeholder,
  options,
  error = false,
  helperText,
  disabled = false,
  width = 220,
  onChange,
}: SimpleSelectFieldProps) {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const open = Boolean(anchorEl);

  const handleOpen = (event: MouseEvent<HTMLElement>) => {
    if (disabled) return;
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => setAnchorEl(null);

  return (
    <Box sx={{ width, maxWidth: "100%", flex: "none", position: "relative" }}>
      <Typography sx={formFieldLabelSx}>
        {label}
        {required ? (
          <Box component="span" sx={{ color: "#E7000B", ml: 0.25 }}>
            *
          </Box>
        ) : null}
      </Typography>
      <Button
        fullWidth
        variant="outlined"
        color="inherit"
        // Exclude from dialog branded outlinedPrimary (blue border).
        className="cascade-trigger-field"
        disabled={disabled}
        onClick={handleOpen}
        endIcon={<KeyboardArrowDownIcon />}
        aria-haspopup="listbox"
        aria-expanded={open}
        sx={triggerButtonSx(error, Boolean(value))}
      >
        <Box
          component="span"
          sx={{
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            textAlign: "left",
            flex: 1,
          }}
        >
          {value || placeholder}
        </Box>
      </Button>
      {helperText ? (
        <FormHelperText error={error} sx={{ mx: 0, mt: 0.5 }}>
          {helperText}
        </FormHelperText>
      ) : null}
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        slotProps={{
          paper: {
            sx: {
              mt: 0.25,
              minWidth: anchorEl?.clientWidth ?? 220,
              maxHeight: 196,
              borderRadius: "4px",
              boxShadow: "0 8px 24px rgba(25,37,50,0.16)",
            },
          },
        }}
      >
        {options.map((option) => {
          const selected = option === value;
          return (
            <MenuItem
              key={option}
              selected={selected}
              onClick={() => {
                onChange(option);
                handleClose();
              }}
              sx={{
                fontSize: "0.8125rem",
                color: "#0D0D0D",
                py: 1,
                px: 1.75,
                justifyContent: "space-between",
                "&.Mui-selected": { bgcolor: "#F3F6FE" },
                "&.Mui-selected:hover": { bgcolor: "#F3F6FE" },
                "&:hover": { bgcolor: "#F3F6FE" },
              }}
            >
              <span>{option}</span>
              {selected ? (
                <CheckIcon sx={{ fontSize: 16, color: "#295EF1", ml: 1 }} />
              ) : null}
            </MenuItem>
          );
        })}
      </Menu>
    </Box>
  );
}

type UpdateRecordFieldProps = Readonly<{
  conceptCode: string;
  value: string[];
  objectType: string;
  disabled?: boolean;
  /** Override assessment-record concept fetch (OTO bundled catalog). */
  fetchConcepts?: ConceptFetcher;
  onChange: (next: { conceptCode: string; values: string[] }) => void;
}>;

function UpdateAssessmentRecordField({
  conceptCode,
  value,
  objectType,
  disabled = false,
  fetchConcepts,
  onChange,
}: UpdateRecordFieldProps) {
  const { primaryColor } = useBrandThemeColors();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [dataReloadToken, setDataReloadToken] = useState(0);
  const open = Boolean(anchorEl);
  const trimmedObjectType = objectType.trim();

  // Dimensions: auto-tagging `kind=assessment_record`, or OTO custom fetcher.
  // Load only while the popover is open — not when Add/Edit mounts.
  const { conceptOptions, loading: conceptsLoading } = useConfigurableConcepts(
    trimmedObjectType || null,
    fetchConcepts ?? null,
    fetchConcepts ? null : "assessment_record",
    { enabled: open, reloadToken: dataReloadToken },
  );

  const selectedConcept = useMemo(() => {
    const code = conceptCode.trim();
    if (code) {
      const matched = conceptOptions.find(
        (item) => item.code.toLowerCase() === code.toLowerCase(),
      );
      if (matched) return matched;
    }
    return conceptOptions[0] ?? null;
  }, [conceptCode, conceptOptions]);

  const activeConceptCode = selectedConcept?.code ?? conceptCode.trim();
  const dimensionLabel = selectedConcept
    ? getConceptDisplayLabel(selectedConcept)
    : activeConceptCode;

  const {
    options: valueOptions,
    loading: valuesLoading,
    loadOptions,
  } = usePaginatedConceptValues(activeConceptCode, 50);

  useEffect(() => {
    if (!open || !activeConceptCode) return;
    void loadOptions(undefined, { force: true });
  }, [activeConceptCode, loadOptions, open]);

  const display = formatUpdateAssessmentRecordDisplay(value, dimensionLabel);

  const handleOpen = (event: MouseEvent<HTMLElement>) => {
    if (disabled) return;
    setDataReloadToken((token) => token + 1);
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => setAnchorEl(null);

  const selectDimension = useCallback(
    (nextCode: string) => {
      if (nextCode === activeConceptCode) return;
      onChange({ conceptCode: nextCode, values: [] });
    },
    [activeConceptCode, onChange],
  );

  const toggleValue = useCallback(
    (option: string) => {
      const nextValues = value.includes(option)
        ? value.filter((item) => item !== option)
        : [...value, option];
      onChange({
        conceptCode: activeConceptCode,
        // Fresh array every toggle — never mutate the parent draft in place.
        values: [...nextValues],
      });
    },
    [activeConceptCode, onChange, value],
  );

  return (
    <Box sx={{ flex: 1, minWidth: 0, position: "relative" }}>
      <Typography sx={formFieldLabelSx}>
        Update Assessment Record (If Impact = Y)
      </Typography>
      <Button
        fullWidth
        variant="outlined"
        color="inherit"
        // Exclude from dialog branded outlinedPrimary (blue border).
        className="cascade-trigger-field"
        disabled={disabled || !trimmedObjectType}
        onClick={handleOpen}
        endIcon={<KeyboardArrowDownIcon />}
        aria-haspopup="dialog"
        aria-expanded={open}
        sx={triggerButtonSx(false, Boolean(display))}
      >
        <Box
          component="span"
          sx={{
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            textAlign: "left",
            flex: 1,
          }}
        >
          {display || "Please select assessment record"}
        </Box>
      </Button>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        slotProps={{
          paper: {
            sx: {
              mt: 0.25,
              minWidth: Math.max(anchorEl?.clientWidth ?? 320, 480),
              borderRadius: "4px",
              boxShadow: "0 8px 24px rgba(25,37,50,0.16)",
              overflow: "hidden",
            },
          },
        }}
      >
        <Box sx={{ display: "flex", minHeight: UPDATE_RECORD_COLUMN_HEIGHT }}>
          <Box sx={{ width: 220, flex: "none", pt: 1, display: "flex", flexDirection: "column" }}>
            <Typography
              sx={{
                fontSize: "0.8125rem",
                fontWeight: 500,
                color: "#0D0D0D",
                px: 2,
                pb: 0.75,
              }}
            >
              Dimension
            </Typography>
            <Box sx={updateRecordColumnScrollSx}>
              {conceptsLoading ? (
                <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
                  <CircularProgress size={20} />
                </Box>
              ) : conceptOptions.length === 0 ? (
                <Typography
                  sx={{
                    px: 2,
                    py: 1.5,
                    fontSize: "0.75rem",
                    color: "#99A1AF",
                  }}
                >
                  No dimensions
                </Typography>
              ) : (
                conceptOptions.map((concept) => {
                  const selected =
                    concept.code.toLowerCase() ===
                    activeConceptCode.toLowerCase();
                  const count =
                    selected && value.length > 0 ? value.length : undefined;
                  return (
                    <Box
                      key={concept.code}
                      component="button"
                      type="button"
                      onClick={() => selectDimension(concept.code)}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 1,
                        width: "100%",
                        minHeight: 44,
                        border: "none",
                        boxShadow: selected
                          ? `inset 3px 0 0 ${primaryColor}`
                          : "none",
                        textAlign: "left",
                        px: 2,
                        py: 1.5,
                        cursor: "pointer",
                        bgcolor: selected
                          ? UPDATE_RECORD_OPTION_SELECTED_BG
                          : "transparent",
                        color: selected ? primaryColor : "#0D0D0D",
                        fontSize: "0.875rem",
                        fontWeight: 400,
                        fontFamily: "inherit",
                        transition: "background-color 0.12s ease",
                        "&:hover": {
                          bgcolor: UPDATE_RECORD_OPTION_HOVER_BG,
                        },
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          minWidth: 0,
                          flex: 1,
                        }}
                      >
                        <Box
                          component="span"
                          sx={{
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {getConceptDisplayLabel(concept)}
                        </Box>
                        {typeof count === "number" ? (
                          <Box
                            component="span"
                            sx={{
                              ml: "auto",
                              minWidth: 36,
                              height: 20,
                              px: 1.25,
                              boxSizing: "border-box",
                              borderRadius: 999,
                              bgcolor: "rgba(41,94,241,0.08)",
                              color: primaryColor,
                              fontSize: "0.75rem",
                              fontWeight: 700,
                              display: "inline-flex",
                              alignItems: "center",
                              justifyContent: "center",
                              flexShrink: 0,
                            }}
                          >
                            {count}
                          </Box>
                        ) : null}
                      </Box>
                      <KeyboardArrowRightIcon
                        sx={{
                          fontSize: 14,
                          color: "currentColor",
                          flexShrink: 0,
                          opacity: 0.85,
                        }}
                      />
                    </Box>
                  );
                })
              )}
            </Box>
          </Box>
          <Box
            sx={{
              flex: 1,
              minWidth: 0,
              borderLeft: "1px solid #F0F1F3",
              pt: 1,
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Typography
              sx={{
                fontSize: "0.8125rem",
                fontWeight: 500,
                color: "#0D0D0D",
                px: "18px",
                pb: 0.75,
              }}
            >
              Value
            </Typography>
            <Box sx={updateRecordColumnScrollSx}>
              {!activeConceptCode ||
              (valuesLoading && valueOptions.length === 0) ? (
                <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
                  <CircularProgress size={20} />
                </Box>
              ) : valueOptions.length === 0 ? (
                <Typography
                  sx={{
                    px: "18px",
                    py: 1.5,
                    fontSize: "0.75rem",
                    color: "#99A1AF",
                  }}
                >
                  No matching values
                </Typography>
              ) : (
                valueOptions.map((option) => {
                  const optionKey = option.name?.trim() || option.code;
                  const checked = value.includes(optionKey);
                  return (
                    <Box
                      key={option.code}
                      component="button"
                      type="button"
                      role="checkbox"
                      aria-checked={checked}
                      aria-label={optionKey}
                      onClick={() => toggleValue(optionKey)}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1.5,
                        width: "100%",
                        minHeight: 44,
                        border: "none",
                        bgcolor: "transparent",
                        px: "18px",
                        py: 1.5,
                        cursor: "pointer",
                        textAlign: "left",
                        fontFamily: "inherit",
                        "&:hover": { bgcolor: "#F9FAFB" },
                      }}
                    >
                      <Box
                        aria-hidden
                        sx={{
                          width: 16,
                          height: 16,
                          borderRadius: "3px",
                          flexShrink: 0,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          border: checked
                            ? `1.5px solid ${primaryColor}`
                            : "1.5px solid #D1D5DC",
                          bgcolor: checked ? primaryColor : "#fff",
                          boxSizing: "border-box",
                        }}
                      >
                        {checked ? (
                          <CheckIcon sx={{ fontSize: 11, color: "#fff" }} />
                        ) : null}
                      </Box>
                      <Typography
                        sx={{
                          fontSize: "0.875rem",
                          color: "#0D0D0D",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {optionKey}
                      </Typography>
                    </Box>
                  );
                })
              )}
            </Box>
          </Box>
        </Box>
      </Popover>
    </Box>
  );
}

type LayerSelectFieldProps = Readonly<{
  value: string;
  error?: boolean;
  helperText?: string;
  disabled?: boolean;
  width?: number | string;
  onChange: (next: string) => void;
}>;

/** Mandatory Layer dropdown — Layer 0, Layer 1, … (AC2); info explains AC3 order. */
function LayerSelectField({
  value,
  error = false,
  helperText,
  disabled = false,
  width = 160,
  onChange,
}: LayerSelectFieldProps) {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const open = Boolean(anchorEl);
  const options = useMemo(() => getClassificationLayerOptions(), []);
  const displayValue = formatClassificationLayerLabel(value);

  const handleOpen = (event: MouseEvent<HTMLElement>) => {
    if (disabled) return;
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => setAnchorEl(null);

  return (
    <Box sx={{ width, maxWidth: "100%", flex: "none", position: "relative" }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          mb: 0.75,
          minHeight: "1.25rem",
        }}
      >
        <Typography sx={{ ...formFieldLabelSx, mb: 0 }}>
          Layer
          <Box component="span" sx={{ color: "#E7000B", ml: 0.25 }}>
            *
          </Box>
        </Typography>
        <Tooltip
          title={CLASSIFICATION_LAYER_INFO}
          arrow
          placement="top"
          enterTouchDelay={0}
          slotProps={AI_PLAN_TOOLTIP_SLOT_PROPS}
        >
          <IconButton
            size="small"
            aria-label="Layer execution order hint"
            sx={{
              ml: 0.25,
              p: 0.25,
              color: "#9CA3AF",
              "&:hover": { color: "#6B7280", bgcolor: "transparent" },
            }}
          >
            <InfoOutlinedIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Tooltip>
      </Box>
      <Button
        fullWidth
        variant="outlined"
        color="inherit"
        className="cascade-trigger-field"
        disabled={disabled}
        onClick={handleOpen}
        endIcon={<KeyboardArrowDownIcon />}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label="Layer"
        sx={triggerButtonSx(error, Boolean(displayValue))}
      >
        <Box
          component="span"
          sx={{
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            textAlign: "left",
            flex: 1,
          }}
        >
          {displayValue || "Please select layer"}
        </Box>
      </Button>
      {helperText ? (
        <FormHelperText error={error} sx={{ mx: 0, mt: 0.5 }}>
          {helperText}
        </FormHelperText>
      ) : null}
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        slotProps={{
          paper: {
            sx: {
              mt: 0.25,
              minWidth: anchorEl?.clientWidth ?? 160,
              maxHeight: 196,
              borderRadius: "4px",
              boxShadow: "0 8px 24px rgba(25,37,50,0.16)",
            },
          },
        }}
      >
        {options.map((option) => {
          const selected = option === value;
          const label = formatClassificationLayerLabel(option);
          return (
            <MenuItem
              key={option}
              selected={selected}
              onClick={() => {
                onChange(option);
                handleClose();
              }}
              sx={{
                fontSize: "0.8125rem",
                color: "#0D0D0D",
                py: 1,
                px: 1.75,
                justifyContent: "space-between",
                "&.Mui-selected": { bgcolor: "#F3F6FE" },
                "&.Mui-selected:hover": { bgcolor: "#F3F6FE" },
                "&:hover": { bgcolor: "#F3F6FE" },
              }}
            >
              <span>{label}</span>
              {selected ? (
                <CheckIcon sx={{ fontSize: 16, color: "#295EF1", ml: 1 }} />
              ) : null}
            </MenuItem>
          );
        })}
      </Menu>
    </Box>
  );
}

export type ClassificationPlanConfigFieldsProps = Readonly<{
  value: ClassificationPlanConfigState;
  objectType: string;
  objectTypeName?: string;
  disabled?: boolean;
  /** Defaults to full Classification template list; OTO passes Implementation only. */
  assessmentTemplateOptions?: readonly string[];
  /**
   * Temporarily hidden on Classification Add/Edit (pass `true` to show).
   * When omitted/`false`, the field is not rendered.
   */
  showTaggingApproach?: boolean;
  /**
   * Temporarily hidden on Classification Add/Edit (pass `true` to show).
   * Layer replaces Execution for run order.
   */
  showExecution?: boolean;
  /** Classification Add/Edit only; OTO hides Layer. */
  showLayer?: boolean;
  /** Override Update Assessment Record concept fetch (OTO catalog). */
  fetchAssessmentRecordConcepts?: ConceptFetcher;
  errors?: Partial<
    Record<
      "taggingApproach" | "execution" | "layer" | "assessmentTemplate",
      string
    >
  >;
  onChange: (patch: Partial<ClassificationPlanConfigState>) => void;
}>;

export default function ClassificationPlanConfigFields({
  value,
  objectType,
  objectTypeName,
  disabled = false,
  assessmentTemplateOptions = ASSESSMENT_TEMPLATE_OPTIONS,
  showTaggingApproach = false,
  showExecution = false,
  showLayer = true,
  fetchAssessmentRecordConcepts,
  errors,
  onChange,
}: ClassificationPlanConfigFieldsProps) {
  // Tagging Approach / Execution are hidden for now; keep props for easy re-enable.
  const showTagApproach = showTaggingApproach === true;
  const showUpdate = shouldShowUpdateRecord(value.assessmentTemplate);

  return (
    <Box
      sx={{
        display: "flex",
        flexWrap: "wrap",
        gap: 2,
        alignItems: "flex-start",
      }}
    >
      {showTagApproach ? (
        <SimpleSelectField
          label="Tagging Approach"
          required
          value={value.taggingApproach}
          placeholder="Please select tagging approach"
          options={TAGGING_APPROACH_OPTIONS}
          error={Boolean(errors?.taggingApproach)}
          helperText={errors?.taggingApproach}
          disabled={disabled}
          onChange={(next) => onChange({ taggingApproach: next })}
        />
      ) : null}
      {showExecution ? (
        <SimpleSelectField
          label="Execution"
          required
          value={value.execution}
          placeholder="Please select execution"
          options={EXECUTION_OPTIONS}
          error={Boolean(errors?.execution)}
          helperText={errors?.execution}
          disabled={disabled}
          onChange={(next) => onChange({ execution: next })}
        />
      ) : null}
      {showLayer ? (
        <LayerSelectField
          value={value.layer}
          error={Boolean(errors?.layer)}
          helperText={errors?.layer}
          disabled={disabled}
          onChange={(next) => onChange({ layer: next })}
        />
      ) : null}
      <SimpleSelectField
        label="Assessment Template"
        required
        value={value.assessmentTemplate}
        placeholder="Please select assessment type"
        options={assessmentTemplateOptions}
        error={Boolean(errors?.assessmentTemplate)}
        helperText={errors?.assessmentTemplate}
        disabled={disabled}
        onChange={(next) =>
          onChange({
            assessmentTemplate: next,
            ...(next === "Tagging"
              ? {
                  updateAssessmentRecordConcept: "",
                  updateAssessmentRecords: [],
                }
              : {}),
          })
        }
      />
      {showUpdate ? (
        <UpdateAssessmentRecordField
          conceptCode={value.updateAssessmentRecordConcept}
          value={value.updateAssessmentRecords}
          objectType={objectType}
          disabled={disabled}
          fetchConcepts={fetchAssessmentRecordConcepts}
          onChange={(next) =>
            onChange({
              updateAssessmentRecordConcept: next.conceptCode,
              updateAssessmentRecords: next.values,
            })
          }
        />
      ) : null}
    </Box>
  );
}

/** Circular gray arrow between Reference and Impacted Items (Concept V3). */
export function ClassificationPlanFlowArrow() {
  return (
    <Box
      aria-hidden
      sx={{
        width: 38,
        height: 38,
        borderRadius: "999px",
        bgcolor: "#F3F4F6",
        color: "#404040",
        flexShrink: 0,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        alignSelf: "center",
        mt: { xs: 0, md: "22px" },
      }}
    >
      <Box
        component="svg"
        width={16}
        height={16}
        viewBox="0 0 24 24"
        fill="none"
        sx={{ display: "block" }}
      >
        <path
          d="M5 12h14M13 6l6 6-6 6"
          stroke="currentColor"
          strokeWidth={2}
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </Box>
    </Box>
  );
}
